import { Box, BoxProps } from '@evaneos/b2b-ui';
import { formatDate } from '@evaneos/b2b-ui/legacy';
import { isAfter } from 'date-fns';
import { useState } from 'react';

import { DayPickerFormInput } from './DayPickerFormInput';
import { DateRange } from './types';

/**
 * Props for the DateRangeFromInputs component
 */
type Props = {
    /** Initial value for the start date input */
    initialStartDate?: Date;
    /** Initial value for the end date input */
    initialEndDate?: Date;
    /** Callback fired when either date changes. Receives the complete date range. */
    onDateRangeChange: (range: DateRange) => void;
    /** Label displayed above the start date input */
    startDateLabel?: string;
    /** Label displayed above the end date input */
    endDateLabel?: string;
    /** CSS class name applied to each input wrapper box */
    inputWrapperClassName?: string;
    /** CSS class name applied to the container box */
    containerClassName?: string;
    /** MUI sx prop applied to the container box */
    containerSx?: BoxProps['sx'];
    /** MUI sx prop applied to each input wrapper box */
    inputWrapperSx?: BoxProps['sx'];
    /**
     * When true, the calendar calculates its height based on 6 weeks to prevent height changes
     * when switching between months. Useful for preventing layout shifts in popovers.
     */
    fixedWeeks?: boolean;
};

/**
 * A date range picker component with separate inputs for start and end dates.
 *
 * Features:
 * - Two date picker inputs (start date and end date)
 * - Automatic validation: start date cannot be after end date
 * - When start date is set after end date, end date is automatically adjusted
 * - Start date is restricted to today or later
 * - Calls onDateRangeChange whenever either date changes
 *
 * @example
 * ```tsx
 * <DateRangeFromInputs
 *   initialStartDate={new Date()}
 *   initialEndDate={addDays(new Date(), 7)}
 *   onDateRangeChange={(range) => console.log(range)}
 *   startDateLabel="Arrival"
 *   endDateLabel="Departure"
 *   fixedWeeks
 * />
 * ```
 */
export function DateRangeFromInputs({
    initialStartDate,
    initialEndDate,
    onDateRangeChange,
    startDateLabel,
    endDateLabel,
    inputWrapperClassName,
    containerClassName,
    containerSx,
    inputWrapperSx,
    fixedWeeks,
}: Props) {
    const [startDate, setStartDate] = useState<Date | undefined>(initialStartDate);
    const [endDate, setEndDate] = useState<Date | undefined>(initialEndDate);

    const handleStartDateChange = (date?: Date) => {
        if (!date) {
            return;
        }

        setStartDate(date);
        if (!!endDate && isAfter(date, endDate)) {
            setEndDate(date);
            onDateRangeChange({ from: date, to: date });
        } else {
            onDateRangeChange({ from: date, to: endDate });
        }
    };
    const handleEndDateChange = (date?: Date) => {
        if (!date) {
            return;
        }

        setEndDate(date);
        onDateRangeChange({ from: startDate, to: date });
    };

    return (
        <Box className={containerClassName} sx={containerSx}>
            <Box className={inputWrapperClassName} sx={inputWrapperSx}>
                <DayPickerFormInput
                    name="start-date"
                    label={startDateLabel}
                    onDayChange={handleStartDateChange}
                    fromDate={new Date()}
                    format="dd/MM/yyyy"
                    fixedWeeks={fixedWeeks}
                    inputProps={{
                        value: startDate ? formatDate(startDate, 'dd/MM/yyyy') : '',
                        // @ts-ignore
                        'data-testid': 'input-start-date',
                    }}
                />
            </Box>
            <Box className={inputWrapperClassName} sx={inputWrapperSx}>
                <DayPickerFormInput
                    name="end-date"
                    label={endDateLabel}
                    onDayChange={handleEndDateChange}
                    fromDate={startDate}
                    format="dd/MM/yyyy"
                    fixedWeeks={fixedWeeks}
                    data-testid="input-end-date"
                    inputProps={{
                        value: endDate ? formatDate(endDate, 'dd/MM/yyyy') : '',
                        // @ts-ignore
                        'data-testid': 'input-end-date',
                    }}
                />
            </Box>
        </Box>
    );
}
